import { useEffect, useState } from 'react';
import { AdminTagsStream } from '@apis/adminTagsStream';
import type { IAdminTag } from '@interfaces/IAdminTag';

export type StreamStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface IAdminTagsStreamState {
  tags: IAdminTag[];
  status: StreamStatus;
  /** True until the first `tags` frame arrives (snapshot loaded). */
  loaded: boolean;
}

/**
 * Subscribes to the live admin-tags SSE stream for a site. Each `tags` frame is
 * a full snapshot that replaces the held list (replace semantics). Held in local
 * state — the data only matters while the page is open.
 */
export function useAdminTagsStream(siteId: string | undefined): IAdminTagsStreamState {
  const [tags, setTags] = useState<IAdminTag[]>([]);
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!siteId) return;

    const stream = new AdminTagsStream(siteId, {
      onEvent: (event) => {
        if (event.channel === 'tags') {
          setTags(event.data);
          setLoaded(true);
        }
        // _heartbeat / _resync need no action: every frame is a full snapshot.
      },
      onTokenExpiring: () => {
        // The stream auto-reconnects with a refreshed token; nothing to do here.
      },
      onOpen: () => setStatus('open'),
      onClose: () => setStatus('closed'),
      onError: () => setStatus('reconnecting'),
    });
    void stream.open();

    // Reset on unmount / siteId change. Runs before the next effect, so the new
    // site starts from a clean snapshot — without a setState in the effect body.
    return () => {
      stream.close();
      setTags([]);
      setLoaded(false);
      setStatus('connecting');
    };
  }, [siteId]);

  return { tags, status, loaded };
}
